/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import {
    CashRegistersAction,
    CashRegistersActionType,
    fetchCashRegistersListSuccessAction,
    fetchCashRegistersListErrorAction,
    downloadCashRegistersListAsExcelSuccessAction,
    downloadCashRegistersListAsExcelErrorAction,
} from '../actions/cashRegistersActions';
import { CashRegisterModelsListRequestParams } from '../api/cashRegisters/cashRegistersClient.typings';
import { CashRegistersState } from '../reducers/cashRegisersReducer';
import { switchMap, catchError, map, exhaustMap, tap, mapTo, takeUntil } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { cashRegistersClient } from '../api/cashRegisters/cashRegistersClient';
import { AxiosError } from 'axios';
import { downloadDataAsFile, parseError } from '../../utils/utils';
import { i18n } from '../translation/i18n';

export const fetchCashRegistersEpic: Epic<CashRegistersAction> = (action$, state) => {
    return action$.pipe(
        ofType(
            CashRegistersActionType.OPEN_REGISTRY_CASH_REGISTERS_LIST_PAGE,
            CashRegistersActionType.APPLY_PAGING,
            CashRegistersActionType.APPLY_FILTERS,
            CashRegistersActionType.APPLY_SORTING,
        ),
        switchMap(() => {
            const queryParams = makeCashRegistersListRequestParamsFromState(state.value.cashRegistersReducer);
            return from(cashRegistersClient.listCashRegistersModels(queryParams)).pipe(
                map((list) => fetchCashRegistersListSuccessAction(list)),
                catchError((err: AxiosError) =>
                    of(
                        fetchCashRegistersListErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.cashRegistersModelsListReqFailed')),
                        ),
                    ),
                ),

                takeUntil(action$.ofType(CashRegistersActionType.EXIT_REGISTRY_CASH_REGISTERS_LIST_PAGE)),
            );
        }),
    );
};

export const cashRegistersListDownloadEpic: Epic<CashRegistersAction> = (actions$, state$) => {
    return actions$.pipe(
        ofType(CashRegistersActionType.DOWNLOAD_EXCEL),
        exhaustMap(() => {
            const fileName = 'cashRegistersList.xlsx';
            const queryParams = { ...state$.value.cashRegistersReducer.filterParams };

            return from(cashRegistersClient.downloadCashRegisterModelsAsExcel(queryParams)).pipe(
                tap((fileData) => downloadDataAsFile(fileName, fileData)),
                mapTo(downloadCashRegistersListAsExcelSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadCashRegistersListAsExcelErrorAction(
                            parseError(err, i18n.t('errors.failedUploadFileName', { fileName })),
                        ),
                    ),
                ),
            );
        }),
    );
};

function makeCashRegistersListRequestParamsFromState(state: CashRegistersState): CashRegisterModelsListRequestParams {
    return {
        limit: state.pageParams.limit,
        offset: state.pageParams.offset,
        sort: state.sortParams.sort,
        ...state.filterParams,
    };
}
